import Phaser from 'phaser';
import { ASSET_KEYS, ASSET_PATHS, COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import { bookCatalog } from '@/data/bookCatalog';
import { courseCatalog } from '@/data/courseCatalog';
import { eventKatalog } from '@/data/eventCatalog';
import { konkurrentKatalog, type KonkurrentRaw } from '@/data/konkurrenter';
import { tidligSpillKatalog } from '@/data/tidligSpillKatalog';
import { lastKaffeprat, type KaffepratData } from '@/systems/kaffeprat';
import { alleForfatterPortretter } from '@/data/portretter';
import { gameState } from '@/state/store';
import { bootstrapBjornArvHvisNy } from '@/state/bjornArv';
import type { BookRaw, CourseRaw, EventRaw } from '@/data/types';

const BOOKS_URL = 'data/books.json';
const COURSES_URL = 'data/courses.json';
const EVENTS_URL = 'data/events.json';
const KONKURRENTER_URL = 'data/konkurrenter.json';
const TIDLIGSPILL_URL = 'data/tidligspill.json';
const KAFFEPRAT_URL = 'data/kaffeprat.json';

export class PreloadScene extends Phaser.Scene {
  constructor() {
    super('Preload');
  }

  preload(): void {
    this.cameras.main.setBackgroundColor(COLORS.beige);

    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;

    this.add
      .text(cx, cy - 60, 'Forlagshuset', {
        fontFamily: FONTS.display,
        fontSize: fontStr(88),
        color: '#862736',
      })
      .setOrigin(0.5);

    this.add
      .text(cx, cy + 20, 'laster manuskripter …', {
        fontFamily: FONTS.body,
        fontSize: fontStr(34),
        color: '#1a1a1a',
      })
      .setOrigin(0.5);

    const barW = 520;
    const barH = 14;
    const barX = cx - barW / 2;
    const barY = cy + 90;

    const frame = this.add.rectangle(cx, barY + barH / 2, barW + 8, barH + 8, 0x000000, 0);
    frame.setStrokeStyle(2, COLORS.ink, 0.6);

    const fill = this.add.rectangle(barX, barY, 1, barH, COLORS.wine).setOrigin(0, 0);

    this.load.on('progress', (value: number) => {
      fill.width = Math.max(1, barW * value);
    });

    for (const [key, path] of Object.entries(ASSET_PATHS)) {
      this.load.image(key, path);
    }

    for (const portrett of alleForfatterPortretter()) {
      this.load.image(portrett.key, portrett.path);
    }

    const EVENT_BILDER: Array<{ key: string; path: string }> = [
      { key: 'event_brage-nominasjon', path: 'assets/events/event_brage-nominasjon_01.webp' },
      { key: 'event_brage-vinner', path: 'assets/events/event_brage-vinner_01.webp' },
      { key: 'event_plagiat-skandale', path: 'assets/events/event_plagiat-skandale_01.webp' },
      { key: 'event_telefonen-ringer', path: 'assets/events/event_telefonen-ringer_01.webp' },
      { key: 'event_nytt-manus-i-posten', path: 'assets/events/event_nytt-manus-i-posten_01.webp' },
    ];
    for (const b of EVENT_BILDER) {
      this.load.image(b.key, b.path);
    }

    this.load.on('loaderror', (file: Phaser.Loader.File) => {
      // Manglende portretter/events er ikke kritisk — fallback håndteres i UI
      console.warn(`Lastefeil: ${file.key} (${file.url})`);
    });

    this.load.json('books-json', BOOKS_URL);
    this.load.json('courses-json', COURSES_URL);
    this.load.json('events-json', EVENTS_URL);
    this.load.json('konkurrenter-json', KONKURRENTER_URL);
    this.load.json('tidligspill-json', TIDLIGSPILL_URL);
    this.load.json('kaffeprat-json', KAFFEPRAT_URL);
  }

  async create(): Promise<void> {
    const raw = this.cache.json.get('books-json') as BookRaw[] | undefined;
    if (Array.isArray(raw)) {
      bookCatalog.loadFromArray(raw);
    } else {
      try {
        await bookCatalog.loadFromUrl(BOOKS_URL);
      } catch (err) {
        console.error('Klarte ikke laste bok-katalog', err);
      }
    }

    const rawCourses = this.cache.json.get('courses-json') as CourseRaw[] | undefined;
    if (Array.isArray(rawCourses)) {
      courseCatalog.loadFromArray(rawCourses);
    } else {
      try {
        await courseCatalog.load(this);
      } catch (err) {
        console.error('Klarte ikke laste kurs-katalog', err);
      }
    }

    const rawEvents = this.cache.json.get('events-json') as EventRaw[] | undefined;
    if (Array.isArray(rawEvents)) {
      eventKatalog.loadFromArray(rawEvents);
    } else {
      try {
        const res = await fetch(EVENTS_URL);
        if (res.ok) {
          const data = (await res.json()) as EventRaw[];
          eventKatalog.loadFromArray(data);
        }
      } catch (err) {
        console.warn('Klarte ikke laste event-katalog', err);
      }
    }

    const rawKonk = this.cache.json.get('konkurrenter-json') as KonkurrentRaw[] | undefined;
    if (Array.isArray(rawKonk)) {
      konkurrentKatalog.loadFromArray(rawKonk);
    } else {
      try {
        const res = await fetch(KONKURRENTER_URL);
        if (res.ok) {
          const data = (await res.json()) as KonkurrentRaw[];
          konkurrentKatalog.loadFromArray(data);
        }
      } catch (err) {
        console.warn('Klarte ikke laste konkurrent-katalog', err);
      }
    }

    const rawTidlig = this.cache.json.get('tidligspill-json') as EventRaw[] | undefined;
    if (Array.isArray(rawTidlig)) {
      tidligSpillKatalog.loadFromArray(rawTidlig);
    } else {
      try {
        const res = await fetch(TIDLIGSPILL_URL);
        if (res.ok) {
          const data = (await res.json()) as EventRaw[];
          tidligSpillKatalog.loadFromArray(data);
        }
      } catch (err) {
        console.warn('Klarte ikke laste tidlig-spill-katalog', err);
      }
    }

    const rawKaffe = this.cache.json.get('kaffeprat-json') as KaffepratData | undefined;
    if (rawKaffe && typeof rawKaffe === 'object') {
      lastKaffeprat(rawKaffe);
    } else {
      try {
        const res = await fetch(KAFFEPRAT_URL);
        if (res.ok) {
          const data = (await res.json()) as KaffepratData;
          lastKaffeprat(data);
        }
      } catch (err) {
        console.warn('Klarte ikke laste kaffeprat', err);
      }
    }

    bootstrapBjornArvHvisNy(gameState);
    gameState.bootstrapInnboksHvisTom();

    this.scene.start('Forlagshuset');
    this.scene.launch('HUD');
  }
}

void ASSET_KEYS;
